/**
 * Local visual tokens for the modern bike station popup.
 *
 * Light values mirror the stop popup design (translucent chips over the map).
 * Dark values map to the shared overlay palette.
 */

import { getUIColorPalette } from "../../../theme/ColorPalette";

export interface BikeStationPopupTheme {

    /** Optional body background; undefined keeps the body transparent over the map in light mode. */
    "bodyBackground": string | undefined;

    /** Background for vehicle type chips. */
    "chipBackground": string;

    /** Border color for vehicle type chips. */
    "chipBorder": string;

    /** Text color for the popup header. */
    "headerText": string;

    /** Primary text color. */
    "primaryText": string;

    /** Secondary/muted text color. */
    "secondaryText": string;

    /** Muted text color for placeholders. */
    "mutedText": string;

    /** Error text color. */
    "errorText": string;

    /** Brand accent used for the header stripe, capacity bar, and action. */
    "brandAccent": string;

    /** Background for the top-right icon action button. */
    "iconButtonBackground": string;

    /** Hover background for the top-right icon action button. */
    "iconButtonHover": string;

    /** Capacity bar fill color. */
    "capacityBarFill": string;

    /** Capacity bar track color. */
    "capacityBarBg": string;

    /** Text color drawn on top of the capacity bar fill. */
    "capacityBarText": string;

    /** Hover background for the action button. */
    "hoverBackground": string;

    /** Border color of the popup card. */
    "cardBorder": string;

}

/**
 * Resolves the popup theme for the current UI mode.
 *
 * @param isDarkUi Whether dark overlay palette is active.
 * @returns Popup theme tokens.
 */
export const getBikeStationPopupTheme = ( isDarkUi: boolean ): BikeStationPopupTheme => {
    const palette = getUIColorPalette( isDarkUi );

    if ( isDarkUi ) {
        return {
            "bodyBackground":       palette.overlayCardBackground,
            "chipBackground":       palette.overlayCardBackgroundSoft,
            "chipBorder":           palette.overlayBorder,
            "headerText":           "#FFFFFF",
            "primaryText":          palette.overlayTextPrimary,
            "secondaryText":        palette.overlayTextSecondary,
            "mutedText":            palette.overlayTextSecondary,
            "errorText":            palette.statusAdvanceText,
            "brandAccent":          "#0CC970",
            "iconButtonBackground": palette.overlayCardBackgroundStrong,
            "iconButtonHover":      "#0CC970",
            "capacityBarFill":      "#0CC970",
            "capacityBarBg":        palette.overlayBorder,
            "capacityBarText":      palette.overlayTextPrimary,
            "hoverBackground":      palette.interactiveHoverBackground,
            "cardBorder":           palette.overlayBorder,
        };
    }

    return {
        "bodyBackground":       undefined,
        "chipBackground":       "rgba(255, 255, 255, 0.7)",
        "chipBorder":           "rgba(0, 0, 0, 0.05)",
        "headerText":           "#000000",
        "primaryText":          "#111827",
        "secondaryText":        "#6B7280",
        "mutedText":            "#9CA3AF",
        "errorText":            "#DC2626",
        "brandAccent":          "#0CC970",
        "iconButtonBackground": "rgba(255, 255, 255, 0.98)",
        "iconButtonHover":      "#0CC970",
        "capacityBarFill":      "#0CC970",
        "capacityBarBg":        "rgba(0, 0, 0, 0.1)",
        "capacityBarText":      palette.textOnBrandPrimary,
        "hoverBackground":      "rgba(15, 23, 42, 0.06)",
        "cardBorder":           "rgba(15, 23, 42, 0.08)",
    };
};
